import React from 'react';
import {
  DocsPage,
  HeroSection,
  InfoBox,
  StepsList,
  CalloutCard,
  CTAButton,
  CodeBlock,
  FeatureGrid,
  FeatureCard,
  Badge,
} from '../../components/docs/DocsComponents';

const QuickStart: React.FC = () => {
  return (
    <DocsPage>
      <HeroSection>
        <h1>Quick Start Guide</h1>
        <p className="subtitle">
          Get started with Mavrk.ink in minutes! Create tokens for free, bridge assets across chains, and manage your portfolio with our comprehensive DeFi platform.
        </p>
      </HeroSection>

      <InfoBox variant="success">
        <div className="info-title">Prerequisites</div>
        <ul>
          <li><strong>Web3 Wallet (Optional):</strong> MetaMask, WalletConnect, or compatible wallet for full features</li>
          <li><strong>ETH (Optional):</strong> Only needed for self-funded deployment or bridging operations</li>
          <li><strong>Modern Browser:</strong> Chrome, Firefox, Safari, or Edge with Web3 support</li>
          <li><strong>No Capital Required:</strong> Use gasless deployment for completely free token creation!</li>
        </ul>
      </InfoBox>

      <h2>🚀 Getting Started</h2>

      <h3>Step 1: Access the Platform</h3>
      <p>
        Navigate to <strong>Mavrk.ink</strong> and explore the four main sections of the platform:
      </p>

      <FeatureGrid>
        <FeatureCard>
          <span className="icon">🔍</span>
          <h3>Explore</h3>
          <p>Discover and trade newly created tokens on Ink L2 with real-time pricing.</p>
        </FeatureCard>

        <FeatureCard>
          <span className="icon">🌉</span>
          <h3>Bridge & Swap</h3>
          <p>Cross-chain operations and trading powered by Relay Protocol.</p>
        </FeatureCard>

        <FeatureCard>
          <span className="icon">👤</span>
          <h3>Profile</h3>
          <p>Manage your wallet, track creator earnings, and lock tokens.</p>
        </FeatureCard>

        <FeatureCard>
          <span className="icon">📖</span>
          <h3>Docs</h3>
          <p>Comprehensive documentation, guides, and technical references.</p>
        </FeatureCard>
      </FeatureGrid>

      <InfoBox variant="info">
        <div className="info-title">No Wallet Required</div>
        <p>
          You can explore the platform and even create tokens without connecting a wallet using our gasless deployment option. The temporary wallet is managed by Gelato for secure, sponsored transactions.
        </p>
      </InfoBox>

      <h3>Step 2: Connect Your Wallet</h3>

      <h4>Option A: Connect Existing Wallet</h4>
      <StepsList>
        <li>
          <strong>Click Connect Wallet:</strong> Located in the top right corner of the platform.
        </li>
        <li>
          <strong>Select Provider:</strong> Choose MetaMask, WalletConnect, or another supported wallet.
        </li>
        <li>
          <strong>Approve Connection:</strong> Confirm the connection request in your wallet.
        </li>
        <li>
          <strong>Switch to Ink L2:</strong> The platform will automatically prompt you to add and switch to the Ink network.
        </li>
      </StepsList>

      <h4>Option B: Use Gasless Deployment (No Wallet Needed)</h4>
      <p>
        Skip wallet connection and proceed directly to token creation. The platform will generate a temporary wallet managed by Gelato for gasless transactions. You can claim ownership later by importing the private key into your wallet.
      </p>

      <InfoBox variant="warning">
        <div className="info-title">Important: Save Your Private Key</div>
        <p>
          If you use gasless deployment, make sure to save the private key provided after token creation. This is your only way to regain control of the token creator address and collect earnings.
        </p>
      </InfoBox>

      <h3>Step 3: Create Your First Token</h3>

      <h4>Configure Token Details</h4>
      <StepsList>
        <li>
          <strong>Navigate to Create:</strong> Click the "Create Token" button or navigate to the creation page.
        </li>
        <li>
          <strong>Enter Token Info:</strong> Provide token name, symbol, description, and social links.
        </li>
        <li>
          <strong>Upload Assets:</strong> Add a token logo (JPG, PNG, GIF) and optional banner image.
        </li>
        <li>
          <strong>Select Deployment Type:</strong> Choose between gasless (free) or self-funded deployment.
        </li>
      </StepsList>

      <h4>Choose Deployment Method</h4>
      
      <p><strong>Gasless Deployment (Recommended for beginners):</strong></p>
      <ul>
        <li>✅ Completely free - no gas fees required</li>
        <li>✅ Powered by Gelato ERC-2771 sponsored transactions</li>
        <li>✅ Temporary wallet managed for you</li>
        <li>✅ Private key provided after deployment</li>
        <li>⚠️ Must save private key to access creator earnings</li>
      </ul>

      <p><strong>Self-Funded Deployment (For experienced users):</strong></p>
      <ul>
        <li>✅ Full control with your connected wallet</li>
        <li>✅ No need to manage separate private keys</li>
        <li>✅ Immediate access to creator earnings</li>
        <li>⚠️ Requires small amount of ETH for gas (~$0.01 on Ink L2)</li>
      </ul>

      <h4>Deploy Your Token</h4>
      <StepsList>
        <li>
          <strong>Review Details:</strong> Double-check all token information before deployment.
        </li>
        <li>
          <strong>Click Deploy:</strong> Initiate the deployment process.
        </li>
        <li>
          <strong>Wait for Confirmation:</strong> The transaction typically takes 10-30 seconds on Ink L2.
        </li>
        <li>
          <strong>Save Private Key:</strong> If using gasless deployment, immediately save the private key provided.
        </li>
        <li>
          <strong>View Your Token:</strong> Your token is now live with automatic liquidity and trading enabled!
        </li>
      </StepsList>

      <InfoBox variant="purple">
        <div className="info-title">What Happens After Deployment?</div>
        <ul>
          <li><strong>Token Creation:</strong> ERC-20 token deployed with Gelato ERC-2771 support</li>
          <li><strong>Liquidity Addition:</strong> 100% of supply added to Uniswap V3 concentrated liquidity pool</li>
          <li><strong>LP Locking:</strong> LP NFT automatically transferred to MavrkLocker for permanent security</li>
          <li><strong>Fee Distribution:</strong> 25% of trading fees routed to creator, 75% to platform</li>
          <li><strong>Trading Enabled:</strong> Immediate trading availability on the Explore page</li>
        </ul>
      </InfoBox>

      <h3>Step 4: Bridge Assets to Ink L2</h3>

      <p>
        Need ETH on Ink L2 for self-funded deployment or trading? Use our integrated Relay Protocol bridge:
      </p>

      <StepsList>
        <li>
          <strong>Navigate to Bridge & Swap:</strong> Access the bridge section from the main menu.
        </li>
        <li>
          <strong>Select Source Chain:</strong> Choose from Ethereum, Base, Polygon, Optimism, Arbitrum, Avalanche, or BSC.
        </li>
        <li>
          <strong>Enter Amount:</strong> Use percentage buttons (25%, 50%, 75%, Max) or enter a custom amount.
        </li>
        <li>
          <strong>Review & Confirm:</strong> Check the transaction details including fees and estimated time.
        </li>
        <li>
          <strong>Track Progress:</strong> Monitor your bridge transaction in real-time with transaction links.
        </li>
      </StepsList>

      <InfoBox variant="info">
        <div className="info-title">Supported Networks</div>
        <p>
          Bridge from any of these networks to Ink L2: Ethereum, Base, Polygon, Optimism, Arbitrum, Avalanche, and Binance Smart Chain. The platform automatically handles network switching.
        </p>
      </InfoBox>

      <h3>Step 5: Manage Your Wallet</h3>

      <p>
        The Profile section provides comprehensive wallet management features:
      </p>

      <h4>Token Holdings</h4>
      <ul>
        <li>View all your ERC-20 tokens with real-time USD values</li>
        <li>Add custom tokens by entering their contract address</li>
        <li>Remove tokens from your portfolio view</li>
        <li>Send and receive tokens to any address</li>
      </ul>

      <h4>Token Locking</h4>
      <ul>
        <li>Lock any ERC-20 token for custom durations using MavrkTokenLocker</li>
        <li>Create multiple locks for the same token</li>
        <li>View all your active locks with countdown timers</li>
        <li>Unlock tokens automatically when the lock period expires</li>
      </ul>

      <h4>Creator Earnings</h4>
      <ul>
        <li>Track earnings from all your created tokens in real-time</li>
        <li>View cumulative earnings across all tokens</li>
        <li>Collect earnings with a single click</li>
        <li>Monitor trading volume and fee generation</li>
      </ul>

      <h3>Step 6: Trade Tokens</h3>

      <p>
        Trading on Mavrk.ink is powered by Uniswap V3 with concentrated liquidity:
      </p>

      <StepsList>
        <li>
          <strong>Browse Tokens:</strong> Explore newly created tokens on the Explore page.
        </li>
        <li>
          <strong>View Details:</strong> Click any token to see price charts, liquidity, and trading info.
        </li>
        <li>
          <strong>Enter Trade Amount:</strong> Specify how much ETH or tokens you want to trade.
        </li>
        <li>
          <strong>Review & Execute:</strong> Check slippage, price impact, and gas fees before confirming.
        </li>
        <li>
          <strong>Track Transaction:</strong> Monitor your trade on the Ink L2 explorer.
        </li>
      </StepsList>

      <h2>💡 Pro Tips</h2>

      <FeatureGrid>
        <FeatureCard>
          <h3>Save Your Private Keys</h3>
          <p>Always save private keys from gasless deployments. Store them securely in a password manager or hardware wallet.</p>
        </FeatureCard>

        <FeatureCard>
          <h3>Use Percentage Buttons</h3>
          <p>When bridging or trading, use the 25%, 50%, 75%, or Max buttons for quick amount selection.</p>
        </FeatureCard>

        <FeatureCard>
          <h3>Monitor Gas Prices</h3>
          <p>While Ink L2 fees are minimal, check gas prices on source chains when bridging from other networks.</p>
        </FeatureCard>

        <FeatureCard>
          <h3>Lock Liquidity</h3>
          <p>Use the token locker to demonstrate long-term commitment and build trust with your community.</p>
        </FeatureCard>
      </FeatureGrid>

      <h2>🎯 Example: Complete Token Launch</h2>

      <p>Here's a complete example workflow for launching a token:</p>

      <CodeBlock>
        <code>{`1. Navigate to Mavrk.ink
2. Click "Create Token"
3. Fill in token details:
   - Name: "My Awesome Token"
   - Symbol: "MAT"
   - Description: "A revolutionary DeFi token"
   - Social links: Twitter, Telegram, Website
4. Upload logo and banner
5. Select "Gasless Deployment"
6. Click "Deploy Token"
7. Wait for confirmation (10-30 seconds)
8. Save the private key provided
9. Token is now live with automatic liquidity!
10. Share on social media and start building community`}</code>
      </CodeBlock>

      <h2>💰 Cost Breakdown</h2>

      <InfoBox variant="success">
        <div className="info-title">Gasless Deployment Costs</div>
        <ul>
          <li><strong>Token Creation:</strong> $0.00 (sponsored by Gelato)</li>
          <li><strong>Liquidity Addition:</strong> $0.00 (included)</li>
          <li><strong>LP Locking:</strong> $0.00 (included)</li>
          <li><strong>Total Cost:</strong> $0.00 🎉</li>
        </ul>
      </InfoBox>

      <InfoBox variant="info">
        <div className="info-title">Self-Funded Deployment Costs</div>
        <ul>
          <li><strong>Token Creation:</strong> ~$0.005 on Ink L2</li>
          <li><strong>Liquidity Addition:</strong> ~$0.003 on Ink L2</li>
          <li><strong>LP Locking:</strong> ~$0.002 on Ink L2</li>
          <li><strong>Total Cost:</strong> ~$0.01 (approximately)</li>
        </ul>
      </InfoBox>

      <h2>🆘 Need Help?</h2>

      <p>
        If you encounter any issues or have questions, check out these resources:
      </p>

      <ul>
        <li><strong>Documentation:</strong> Browse our comprehensive docs for detailed guides</li>
        <li><strong>Discord Community:</strong> Join our Discord for real-time support</li>
        <li><strong>Twitter:</strong> Follow us for updates and announcements</li>
        <li><strong>Telegram:</strong> Chat with the community and team</li>
      </ul>

      <CalloutCard>
        <h3>Ready to Create Your Token?</h3>
        <p>
          You're all set to launch your first token on Mavrk.ink! Click below to get started with completely free token creation.
        </p>
        <CTAButton to="/create">
          Create Your First Token →
        </CTAButton>
      </CalloutCard>

      <InfoBox variant="warning">
        <div className="info-title">Important Disclaimer</div>
        <p>
          Token creation and trading involve significant risks. Always do your own research before investing. This platform is for educational and experimental purposes. Never invest more than you can afford to lose.
        </p>
      </InfoBox>

    </DocsPage>
  );
};

export default QuickStart;
